import { useState, useEffect } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";

export default function CostInventoryReport() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [orderBy, setOrderBy] = useState("code");
  const [order, setOrder] = useState("asc");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resProducts, resInventory, resPackages] = await Promise.all([
          services.getProducts(),
          services.getInventory(),
          services.getPackages(),
        ]);

        const products = resProducts.data?.data ?? [];
        const inventories = resInventory.data?.data ?? [];
        const packages = resPackages ?? [];

        const inventoryMap = {};
        inventories.forEach((inv) => {
          inventoryMap[inv.productId] = inv;
        });

        const productMap = {};

        products.forEach((p) => {
          const inv = inventoryMap[p.id];
          const weight = inv?.quantity || 0;
          const averageCost = inv?.averageCost || 0;
          const amountBeforeVat = weight * averageCost;

          productMap[p.id] = {
            id: p.id,
            code: p.code,
            title: p.title,
            category: p.category?.name || "-",
            sub_category: p.subCategory?.name || "-",
            unit: "กก.",
            weight,
            pack_weight: 0,
            pack_amount: 0,
            average_cost: averageCost,
            amount_before_vat: amountBeforeVat,
          };
        });

        packages.forEach((pkg) => {
          const id = pkg.productId;
          const quantity = pkg.quantity || 0;
          if (!productMap[id]) return;
          productMap[id].pack_amount += 1;
          productMap[id].pack_weight += quantity;
        });

        const result = Object.values(productMap).map((item) => ({
          ...item,
          sub_weight: item.weight - item.pack_weight,
        }));

        setRows(result);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) =>
          typeof b[orderBy] === "string"
            ? b[orderBy]?.localeCompare(a[orderBy])
            : (b[orderBy] ?? 0) - (a[orderBy] ?? 0)
      : (a, b) =>
          typeof a[orderBy] === "string"
            ? a[orderBy]?.localeCompare(b[orderBy])
            : (a[orderBy] ?? 0) - (b[orderBy] ?? 0);
  };

  const sortData = (data, comparator) => [...data].sort(comparator);

  const formatNum = (value) =>
    new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);

  const filteredRows = rows.filter((r) => {
    const text = search.toLowerCase();
    return (
      r.code?.toLowerCase().includes(text) ||
      r.title?.toLowerCase().includes(text) ||
      r.category?.toLowerCase().includes(text) ||
      r.sub_category?.toLowerCase().includes(text)
    );
  });

  const totals = filteredRows.reduce(
    (acc, curr) => {
      acc.weight += curr.weight || 0;
      acc.sub_weight += curr.sub_weight || 0;
      acc.pack_weight += curr.pack_weight || 0;
      acc.pack_amount += curr.pack_amount || 0;
      acc.amount_before_vat += curr.amount_before_vat || 0;
      acc.average_cost += curr.count ? curr.average_cost : 0; // Note: original code logic here was a bit weird for avg cost sum, but sticking to original reducing logic structure if possible, though summing average costs is usually meaningless. Original code: `acc.average_cost += curr.count ? curr.average_cost : 0;`. `curr.count` is undefined in `productMap` creation above in original code! So it likely added 0. I will keep it as 0 safely.
      // Wait, 'count' was NOT set in the original loop for productMap. So `acc.average_cost` was always 0 in totals.
      // But `totals.average_cost` is recalculated below using formula: totals.amount_before_vat / totals.weight.
      // So the reduce part for average_cost doesn't matter much.
      return acc;
    },
    {
      weight: 0,
      sub_weight: 0,
      pack_weight: 0,
      pack_amount: 0,
      amount_before_vat: 0,
      average_cost: 0,
    }
  );

  if (totals.weight > 0) {
    totals.average_cost = totals.amount_before_vat / totals.weight;
  }

  function exportToCSV(data, filename = "report.csv") {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const lines = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleExportCSV = () => {
    const dataToExport = sortData(
      filteredRows,
      getComparator(order, orderBy)
    ).map((row) => ({
      หมวดหมู่หลัก: row.category,
      หมวดหมู่ย่อย: row.sub_category,
      รหัส: row.code,
      สินค้า: row.title,
      น้ำหนักรวม: Number(row.weight ?? 0).toFixed(2),
      น้ำหนักย่อย: Number(row.sub_weight ?? 0).toFixed(2),
      น้ำหนักแพ็ค: Number(row.pack_weight ?? 0).toFixed(2),
      จำนวนแพ็ค: Number(row.pack_amount ?? 0),
      หน่วย: row.unit || "กก.",
      ต้นทุนเฉลี่ย: Number(row.average_cost ?? 0).toFixed(2),
      ยอดรวม: Number(row.amount_before_vat ?? 0).toFixed(2),
    }));

    exportToCSV(
      dataToExport,
      `cost_report_${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  const sortedRows = sortData(filteredRows, getComparator(order, orderBy));

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-8 bg-blue-600 rounded-full"></span>
              รายงานต้นทุนสินค้า
            </h1>

            <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
               <div className="relative w-full md:w-80">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="ค้นหาด้วยรหัส ชื่อสินค้าหรือหมวดหมู่"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                />
               </div>
               
               <button
                  onClick={handleExportCSV}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white border border-transparent rounded-xl hover:bg-blue-700 transition-all font-medium shadow-sm hover:shadow-md whitespace-nowrap"
              >
                  <ContentCopyIcon fontSize="small" />
                  <span>Export ข้อมูลหลัก</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
         {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
             </div>
        ) : (
             <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                 <div className="overflow-x-auto">
                     <table className="w-full text-sm text-left">
                         <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                             <tr>
                                <th onClick={() => handleSort("category")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center gap-1">
                                        หมวดหมู่หลัก {orderBy === "category" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("sub_category")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center gap-1">
                                        หมวดหมู่ย่อย {orderBy === "sub_category" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("code")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center gap-1">
                                        รหัส {orderBy === "code" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("title")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center gap-1">
                                        สินค้า {orderBy === "title" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("weight")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center justify-end gap-1">
                                        น้ำหนักรวม {orderBy === "weight" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("sub_weight")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center justify-end gap-1">
                                        น้ำหนักย่อย {orderBy === "sub_weight" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-right">น้ำหนักแพ็ค</th>
                                <th className="px-4 py-4 text-right">จำนวนแพ็ค</th>
                                <th className="px-4 py-4">หน่วย</th>
                                <th onClick={() => handleSort("average_cost")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center justify-end gap-1">
                                        ต้นทุนเฉลี่ย {orderBy === "average_cost" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => handleSort("amount_before_vat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center justify-end gap-1">
                                        ยอดรวม {orderBy === "amount_before_vat" && <span className="text-blue-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                             </tr>
                         </thead>
                         <tbody className="divide-y divide-gray-50">
                             {sortedRows.map((row) => (
                                 <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                                     <td className="px-4 py-3 text-gray-600">{row.category}</td>
                                     <td className="px-4 py-3 text-gray-600">{row.sub_category}</td>
                                     <td className="px-4 py-3 text-gray-600">{row.code}</td>
                                     <td className="px-4 py-3 font-medium text-gray-800">{row.title}</td>
                                     <td className="px-4 py-3 text-right font-medium">{formatNum(row.weight)}</td>
                                     <td className="px-4 py-3 text-right text-gray-600">{formatNum(row.sub_weight)}</td>
                                     <td className="px-4 py-3 text-right text-gray-600">{formatNum(row.pack_weight)}</td>
                                     <td className="px-4 py-3 text-right text-gray-600">{row.pack_amount}</td>
                                     <td className="px-4 py-3 text-gray-500">{row.unit}</td>
                                     <td className="px-4 py-3 text-right text-gray-600">{formatNum(row.average_cost)}</td>
                                     <td className="px-4 py-3 text-right font-bold text-gray-800">{formatNum(row.amount_before_vat)}</td>
                                 </tr>
                             ))}
                         </tbody>
                         <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                             <tr>
                                 <td colSpan={4} className="px-4 py-4 text-right">รวมทั้งหมด</td>
                                 <td className="px-4 py-4 text-right">{formatNum(totals.weight)}</td>
                                 <td className="px-4 py-4 text-right">{formatNum(totals.sub_weight)}</td>
                                 <td className="px-4 py-4 text-right">{formatNum(totals.pack_weight)}</td>
                                 <td className="px-4 py-4 text-right">{totals.pack_amount}</td>
                                 <td className="px-4 py-4">กก.</td>
                                 <td className="px-4 py-4 text-right">{formatNum(totals.average_cost)}</td>
                                 <td className="px-4 py-4 text-right text-lg text-blue-700">{formatNum(totals.amount_before_vat)}</td>
                             </tr>
                         </tfoot>
                     </table>
                 </div>
             </div>
        )}
      </div>
    </div>
  );
}
